import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { interactionTraces, type InteractionTrace, type TraceStep } from '../api'
import { useAuth } from '../auth'

const PAGE_SIZE = 50

const PATTERN_LABEL: Record<string, string> = {
  top_left: 'Top-left search',
  sort: 'Sort',
  visit: 'Visit',
  wall_create: 'Wall',
  refile: 'Re-file',
}
const PAGE_LABEL: Record<string, string> = {
  main: 'Main',
  stats: 'Stats',
  playbook: 'Playbook',
  privacy: 'Privacy',
}
const SORT_CONTROL_LABEL: Record<string, string> = {
  dial: 'Dial', clock: 'Clock', collapse: 'Collapse', expand: 'Expand', alpha: 'A→Z',
}
const STEP_ICON: Record<string, string> = { folder: '📁', matter: '⬦', card: '▤' }

// Number of follow-up clicks actually captured today. v1 captures the first
// click only; columns 2 + 3 render visible-but-greyed (Ken 2026-08-18) so the
// eventual widening is obvious without a table rearchitecture.
const CAPTURED_STEPS = 1

function TriggerCell({ t }: { t: InteractionTrace }) {
  if (t.pattern === 'top_left') {
    return <span className="me-mono text-[11.5px] me-text">&ldquo;{t.trigger?.text || ''}&rdquo;</span>
  }
  if (t.pattern === 'sort') {
    const c = t.trigger?.control || ''
    const label = SORT_CONTROL_LABEL[c] || c
    return (
      <span className="me-chip text-[10px] uppercase self-start">
        {label}{t.trigger?.value ? ` · ${t.trigger.value}` : ''}
      </span>
    )
  }
  if (t.pattern === 'wall_create') {
    const bits = [t.trigger?.match, t.trigger?.mode, t.trigger?.source].filter(Boolean).join(' · ')
    return <span className="me-chip text-[10px] uppercase self-start">{bits || 'wall'}</span>
  }
  if (t.pattern === 'refile') {
    const tr = (t.trigger || {}) as Record<string, unknown>
    const fm = tr.from_matter_id, tm = tr.to_matter_id
    const from = fm != null ? `M-${String(fm)}` : '—'
    const to = tm != null ? `M-${String(tm)}${tr.to_matter_was_new ? ' (new)' : ''}` : '—'
    const em = tr.artifact_id != null ? `#${String(tr.artifact_id)}` : '#?'
    return <span className="me-mono text-[11px] me-text">{em}: {from} → {to}</span>
  }
  return <span className="me-text-mute">—</span>
}

function StepContent({ step }: { step?: TraceStep }) {
  if (!step) return <span className="me-text-mute">—</span>
  return (
    <span title={`${step.type}: ${step.label}`}>
      <span className="me-text-mute mr-1">{STEP_ICON[step.type] || '•'}</span>
      {step.label || step.type}
    </span>
  )
}

export default function Searches() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  // Builders only: superusers (all tenants) + studio_editors (their tenant).
  const canRead = !!user && (user.is_superuser || user.role === 'studio_editor')

  // Stamp 'last opened Searches' (localStorage) so StudioLayout's nav pip knows
  // what's new since this visit — on open AND on leave, so a trace that lands
  // while viewing still clears once seen. (Ken 2026-08-18.)
  useEffect(() => {
    const stamp = () => { try { localStorage.setItem('me.searches.seenAt', new Date().toISOString()) } catch { /* ignore */ } }
    stamp()
    return stamp
  }, [])

  const [page, setPage] = useState(0)
  const [pattern, setPattern] = useState('')

  const q = useQuery({
    queryKey: ['interactionTraces', { page, pattern }],
    queryFn: () => interactionTraces.listPaged({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...(pattern ? { pattern } : {}),
    }),
    placeholderData: (prev) => prev,
    enabled: canRead,
  })

  if (!canRead) {
    return (
      <div className="p-6 text-[13px] me-text-mute">
        The Searches log is restricted to Studio builders.
      </div>
    )
  }

  const rows = q.data?.results ?? []
  const total = q.data?.count ?? 0
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  const patternTabs: [string, string][] = [
    ['', 'All'], ['top_left', 'Search'], ['sort', 'Sort'], ['visit', 'Visit'], ['wall_create', 'Wall'], ['refile', 'Re-file'],
  ]

  return (
    <div className="me-scroll h-full overflow-auto p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="me-section-h mr-2">Searches</h1>
        {/* Pattern filter */}
        <div className="inline-flex items-center gap-0.5 rounded border me-border p-0.5">
          {patternTabs.map(([val, label]) => (
            <button
              key={val || 'all'}
              onClick={() => { setPattern(val); setPage(0) }}
              data-selected={pattern === val ? 'true' : undefined}
              className="me-tab !text-[11px] !px-2 !py-0.5"
            >
              {label}
            </button>
          ))}
        </div>
        <span className="me-mono text-[11px] me-text-mute">
          {q.isFetching ? 'loading…' : `${total} trace${total === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="me-card overflow-auto">
        <table className="w-full table-fixed text-[12px]">
          <thead className="me-text-mute">
            <tr className="border-b me-border">
              <th className="px-3 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider w-[160px]">When</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider w-[120px]">User</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider w-[84px]">Page</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider w-[130px]">Pattern</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">Trigger</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">→ 1</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">→ 2</th>
              <th className="px-2 py-2 text-left font-normal text-[10.5px] uppercase tracking-wider">→ 3</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t me-border align-top">
                <td className="me-mono px-3 py-1.5 align-top text-[10.5px] me-text-dim whitespace-nowrap"
                    title={new Date(t.created_at).toLocaleString()}>
                  {new Date(t.created_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
                  })}
                </td>
                <td className="px-2 py-1.5 align-top text-[11.5px] me-text truncate">
                  {t.user_name || `#${t.user ?? '—'}`}
                </td>
                <td className="px-2 py-1.5 align-top">
                  <span className="me-chip text-[10px] uppercase">{PAGE_LABEL[t.page] || t.page}</span>
                </td>
                <td className="px-2 py-1.5 align-top text-[11.5px] me-text-mute">
                  {PATTERN_LABEL[t.pattern] || t.pattern}
                </td>
                <td className="px-2 py-1.5 align-top max-w-[260px] truncate">
                  <TriggerCell t={t} />
                </td>
                {/* Step columns. Only the first is captured in v1; the other two
                    render visible-but-greyed so widening capture later is obvious. */}
                {[0, 1, 2].map((i) => {
                  const greyed = i >= CAPTURED_STEPS
                  return (
                    <td
                      key={i}
                      className={`px-2 py-1.5 align-top text-[11.5px] truncate max-w-[220px] ${greyed ? 'opacity-30 me-text-mute' : 'me-text'}`}
                      title={greyed ? 'Not captured yet (v1 records the first click only)' : undefined}
                    >
                      {greyed ? '–' : <StepContent step={t.steps?.[i]} />}
                    </td>
                  )
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-[12px] me-text-mute">No traces match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-2 flex items-center gap-2 text-[11px] me-text-mute">
        <button
          className="me-btn me-btn-outline !px-2 !py-0.5"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ‹ Prev
        </button>
        <span className="me-mono">page {page + 1} / {maxPage + 1}</span>
        <button
          className="me-btn me-btn-outline !px-2 !py-0.5"
          disabled={page >= maxPage}
          onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
        >
          Next ›
        </button>
      </div>
    </div>
  )
}
